if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          mains: this.querySelectorAll(".product-media-main"),
          thumbnails: this.querySelectorAll(
            ".product-media-thumbnail:not(.product-media-thumbnail-variant), .product-media-thumbnail:first-child",
          ),
          slideshow: this.querySelector(".product-media-main-wrapper"),
        };

        if (!this.elements.thumbnails.length) {
          return;
        }

        this.elements.thumbnails.forEach((thumbnail) => {
          const mediaToggle = thumbnail.querySelector("button");
          if (!mediaToggle) {
            return;
          }
          mediaToggle.addEventListener(
            "click",
            this.setActiveMedia.bind(this, mediaToggle.dataset.mediaId, false),
          );
        });
      }

      connectedCallback() {
        if (!window.Flickity) {
          return;
        }

        this.elements.slideshow.style.display = "block";
        this.flickity = new Flickity(this.elements.slideshow, {
          autoPlay: false,
          cellAlign: "center",
          groupCells: false,
          contain: true,
          resize: true,
          draggable: true,
          prevNextButtons: false,
          cellSelector: ".product-media",
          initialIndex: 0,
          pageDots: false,
          wrapAround: true,
          accessibility: false,
          adaptiveHeight: true,
          watchCSS: true,
          on: {
            change: (index) => {
              const { element } = this.flickity.cells[index];
              const mediaId = element.dataset.mediaId;
              this.setActiveMedia(mediaId);
            },
          },
        });
      }

      setActiveMedia(mediaId, prepend) {
        const activeMainMedia = [...this.elements.mains].find((element) => {
          return element.classList.contains("is-active");
        });
        const nextMainMedia = [...this.elements.mains].find((element) => {
          return element.dataset.mediaId === mediaId.toString();
        });
        const index = [...this.elements.mains].indexOf(nextMainMedia);

        if (this.flickity) {
          this.flickity.select(index);
        }

        this.elements.thumbnails.forEach((thumb) => {
          thumb.classList.remove("is-active");
        });
        const thumbnail = [...this.elements.thumbnails].find((element) => {
          return element.dataset.mediaId === mediaId.toString();
        });

        activeMainMedia.classList.remove("is-active");

        nextMainMedia.classList.add("is-active");
        thumbnail?.classList.add("is-active");
        this.playActiveMedia(nextMainMedia);

        if (!thumbnail) {
          // The code below is for thumbnails, return early
          // if there are not visible (e.g. in the quick view drawer).
          return;
        }

        if (prepend) {
          thumbnail.parentElement.prepend(thumbnail);
        } else {
          thumbnail.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });
        }
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector("product-model, deferred-media");

        if (deferredMedia) {
          deferredMedia.loadContent(false);
        }
      }
    },
  );
}

if (!customElements.get("media-zoom")) {
  customElements.define(
    "media-zoom",
    class MediaZoom extends HTMLElement {
      constructor() {
        super();

        this.mode = this.getAttribute("data-mode");
        this.lastEvent = null;
        this.onMouseEnter = this.onMouseEnter.bind(this);
        this.onMouseMove = this.onMouseMove.bind(this);
        this.onMouseLeave = this.onMouseLeave.bind(this);
        this.onClick = this.onClick.bind(this);
      }

      connectedCallback() {
        this.original = this.querySelector("img");
        if (!this.original) {
          return;
        }

        this.original.classList.add("media-zoom-base");

        this.zoomInner = document.createElement("div");
        this.zoomInner.className = "media-zoom-inner";
        this.zoomImg = document.createElement("img");
        this.zoomImg.className = "media-zoom-zoomed";
        this.zoomImg.alt = this.original.alt || "";
        this.zoomInner.appendChild(this.zoomImg);
        this.appendChild(this.zoomInner);

        this.desktopZoomQuery = window.matchMedia("(min-width: 990px)");
        if (this.desktopZoomQuery.matches) {
          this.bindZoomEvents();
        }

        this.onDesktopZoomChange = () => {
          if (this.desktopZoomQuery.matches) {
            this.bindZoomEvents();
          } else {
            this.unbindZoomEvents();
            this.resetZoom();
          }
        };
        this.desktopZoomQuery.addEventListener("change", this.onDesktopZoomChange);

        if (this.mode === "lightbox_zoom_hover") {
          this.original.addEventListener("click", this.onClick);
        }
      }

      disconnectedCallback() {
        this.unbindZoomEvents();
        if (this.desktopZoomQuery && this.onDesktopZoomChange) {
          this.desktopZoomQuery.removeEventListener("change", this.onDesktopZoomChange);
        }
        this.original?.removeEventListener("click", this.onClick);
      }

      bindZoomEvents() {
        if (this.zoomEventsBound || !this.original) {
          return;
        }

        this.original.addEventListener("mouseenter", this.onMouseEnter);
        this.original.addEventListener("mousemove", this.onMouseMove);
        this.original.addEventListener("mouseleave", this.onMouseLeave);
        this.zoomEventsBound = true;
      }

      unbindZoomEvents() {
        if (!this.original || !this.zoomEventsBound) {
          return;
        }

        this.original.removeEventListener("mouseenter", this.onMouseEnter);
        this.original.removeEventListener("mousemove", this.onMouseMove);
        this.original.removeEventListener("mouseleave", this.onMouseLeave);
        this.zoomEventsBound = false;
      }

      resetZoom() {
        this.classList.remove("is-zooming");
        if (this.zoomImg) {
          this.zoomImg.style.transform = "";
        }
      }

      updateInnerZoom(event) {
        if (!event || !this.zoomImg?.complete) {
          return;
        }

        const rect = this.getBoundingClientRect();
        const width = rect.width;
        const height = rect.height;

        if (!width || !height) {
          return;
        }

        const x = Math.max(0, Math.min(event.clientX - rect.left, width));
        const y = Math.max(0, Math.min(event.clientY - rect.top, height));
        const ratioX = x / width;
        const ratioY = y / height;

        const naturalWidth = this.zoomImg.naturalWidth;
        const naturalHeight = this.zoomImg.naturalHeight;

        if (!naturalWidth || !naturalHeight) {
          return;
        }

        const scale = Math.max(naturalWidth / width, naturalHeight / height, 2);
        const zoomWidth = width * scale;
        const zoomHeight = height * scale;

        this.zoomImg.style.width = `${zoomWidth}px`;
        this.zoomImg.style.height = `${zoomHeight}px`;
        this.zoomImg.style.transform = `translate(${-ratioX * (zoomWidth - width)}px, ${-ratioY * (zoomHeight - height)}px)`;
      }

      onMouseEnter(event) {
        this.lastEvent = event;
        this.classList.add("is-zooming");

        if (!this.zoomImg.src) {
          this.zoomImg.src = this.getAttribute("data-zoom");
          this.zoomImg.onload = () => {
            this.updateInnerZoom(this.lastEvent);
          };
        } else {
          this.updateInnerZoom(event);
        }
      }

      onMouseMove(event) {
        this.lastEvent = event;
        this.updateInnerZoom(event);
      }

      onMouseLeave() {
        this.lastEvent = null;
        this.resetZoom();
      }

      onClick() {
        const sectionId = this.getAttribute("data-modal-id");
        const modal = document.querySelector(`#ProductModal-${sectionId}`);

        if (modal) {
          modal.show(this);
        }
      }
    },
  );
}

if (!customElements.get("product-thumbnail-navigation")) {
  customElements.define(
    "product-thumbnail-navigation",
    class ProductThumbnailNavigation extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.init();
      }

      init() {
        this.gallery = document.querySelector(`#${this.getAttribute("for")}`);
        this.elements = {
          gallery: this.gallery,
          scroller: this.gallery.querySelector(".product-media-scroller"),
          thumbnailWrapper: this.gallery.querySelector(".product-media-thumbnails"),
          thumbnails: this.gallery.querySelectorAll(".product-media-thumbnail"),
          buttonPrev: this.querySelector(".js-prev"),
          buttonNext: this.querySelector(".js-next"),
        };

        if (!this.elements.thumbnails.length) {
          return;
        }

        if (this.elements.scroller.clientHeight < this.elements.thumbnailWrapper.scrollHeight) {
          this.classList.remove("hidden");

          this.elements.buttonPrev.addEventListener("click", (event) => {
            event.preventDefault();
            this.prev();
          });

          this.elements.buttonNext.addEventListener("click", (event) => {
            event.preventDefault();
            this.next();
          });

          this.elements.thumbnailWrapper.addEventListener("scroll", () => {
            this.buttonAvailability();
          });
        }
      }

      prev() {
        const scrollPos = this.elements.thumbnailWrapper.scrollTop;
        this.elements.thumbnailWrapper.scroll(0, scrollPos - 300);
      }

      next() {
        const scrollPos = this.elements.thumbnailWrapper.scrollTop;
        this.elements.thumbnailWrapper.scroll(0, scrollPos + 300);
      }

      buttonAvailability() {
        const el = this.elements.thumbnailWrapper;
        const hasBottomed = el.scrollTop + el.clientHeight >= el.scrollHeight - 30;
        const hasTopped = el.scrollTop <= 30;
        this.elements.buttonPrev.classList.remove("pill-nav-item-disabled");
        this.elements.buttonNext.classList.remove("pill-nav-item-disabled");
        this.elements.buttonNext.setAttribute("aria-disabled", "false");
        this.elements.buttonPrev.setAttribute("aria-disabled", "false");

        if (hasBottomed) {
          this.elements.buttonNext.classList.add("pill-nav-item-disabled");
          this.elements.buttonNext.setAttribute("aria-disabled", "true");
        }

        if (hasTopped) {
          this.elements.buttonPrev.classList.add("pill-nav-item-disabled");
          this.elements.buttonPrev.setAttribute("aria-disabled", "true");
        }
      }
    },
  );
}
